import React from "react";
import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { TYPES, linkTo, getTitle, getImage, getBlurb } from "@/config";

const YT_RE = /https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]{11}/;

function hasVideo(item) {
  return YT_RE.test(String(item?.body || "")) || YT_RE.test(String(item?.show_notes || ""));
}

function metaLine(item, ctype) {
  const t = TYPES[ctype];
  if (ctype === "cases") return [item.category, item.era].filter(Boolean).join(" · ");
  if (ctype === "people") return (item.roles || []).join(" · ");
  if (ctype === "artworks") return [item.artist, item.current_status].filter(Boolean).join(" · ");
  if (ctype === "museums") return [item.location].filter(Boolean).join(" · ");
  if (ctype === "episodes") return `Episode ${item.episode_number || ""}`;
  if (ctype === "mishaps") return [item.year, item.location].filter(Boolean).join(" · ");
  if (ctype === "news" || ctype === "essays")
    return item.reading_time || item.source || t.singular;
  return t.singular;
}

export function EntryCard({ item, ctype, testid }) {
  const img = getImage(item);
  const objectPosition = item.image_position || (ctype === "people" ? "center top" : "center");
  return (
    <Link
      to={linkTo(ctype, item.slug)}
      data-testid={testid}
      className="group block border border-line bg-surface overflow-hidden hover:border-gold/60 transition-colors duration-500"
    >
      {img && (
        <div className="relative aspect-[4/3] overflow-hidden bg-elevated">
          <img src={img} alt={getTitle(item)} loading="lazy" style={{ objectPosition }}
            className="card-img h-full w-full object-cover grayscale-[15%]" />
          {hasVideo(item) && (
            <span data-testid="watch-badge"
              className="absolute left-3 top-3 inline-flex items-center gap-1.5 bg-ink/85 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-gold backdrop-blur-sm">
              <PlayCircle size={12} /> Watch
            </span>
          )}
        </div>
      )}
      <div className="p-5">
        <div className="eyebrow mb-2 text-gold/80">{metaLine(item, ctype)}</div>
        <h3 className="font-serif text-2xl leading-tight text-parchment group-hover:text-gold transition-colors duration-300">
          {getTitle(item)}
        </h3>
        {item.subtitle && (
          <p className="mt-1 font-serif text-lg text-faint italic">{item.subtitle}</p>
        )}
        <p className="mt-3 text-sm leading-relaxed text-faint line-clamp-3">{getBlurb(item)}</p>
      </div>
    </Link>
  );
}

export function ListRow({ item, ctype, index, testid }) {
  return (
    <Link
      to={linkTo(ctype, item.slug)}
      data-testid={testid}
      className="group flex items-baseline gap-4 border-b border-line py-4 hover:pl-2 transition-all duration-300"
    >
      {typeof index === "number" && (
        <span className="font-mono text-xs text-faint w-6 shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
      )}
      <div className="min-w-0">
        <h4 className="font-serif text-xl text-parchment group-hover:text-gold transition-colors duration-300 truncate">
          {getTitle(item)}
        </h4>
        <div className="eyebrow mt-1">{metaLine(item, ctype)}</div>
      </div>
    </Link>
  );
}

export { metaLine };
